import { __assign, __awaiter, __generator } from "tslib";
import { AvailabilityCalendar } from '@wix/ambassador-availability-calendar/http';
import { queryServices } from '@wix/ambassador-bookings-services-v2-service/http';
import { ServiceType as ServiceV2Type } from '@wix/ambassador-bookings-services-v2-service/types';
import { queryStaffMembers } from '@wix/ambassador-bookings-staff-v1-staff-member/http';
import { NotificationsServer } from '@wix/ambassador-notifications-server/http';
import { ServicesCatalogServer } from '@wix/ambassador-services-catalog-server/http';
import httpAdapterFactory from '@wix/bookings-adapter-http-api';
import { DailyTimetableSettingsKeys } from '@wix/bookings-app-builder-settings-const/dist/src/DailyTimeTable/Settings.const';
import { getInstance, getServerBaseUrl, } from '@wix/bookings-catalog-calendar-viewer-utils';
import { BookingStatus, createConfig, queryBookings, } from '@wix/bookings-checkout-api';
import { getLocalTimezone } from '@wix/bookings-date-time';
import { mapResponseToBusinessInfo } from '@wix/bookings-uou-mappers';
import { ServiceType, TimezoneType, } from '@wix/bookings-uou-types';
import { getEndOfMonthShiftedRfcString, getShiftedRfcStringDateStart, getStartOfDayInSelectedTimezone, getTodayStartOfDayUTC, } from '../utils/date-time';
import { getFlowApiEnvironment } from '../utils/getFlowApiEnvironment';
import { mapServiceV2ToService, mapStaffV1ToStaffDto } from './legacyMappers';
import { withErrorHandler } from './withErrorHandler';
export var AVAILABILITY_SERVER_URL = '_api/availability-calendar';
export var CATALOG_SERVER_URL = '_api/services-catalog';
export var NOTIFICATIONS_SERVER_URL = '_api/notifications-server';
var BookingsAPI = /** @class */ (function () {
    function BookingsAPI(instance, platformAPIs, wixCodeApi, appParams, experiments, httpClient, errorHandler) {
        var _this = this;
        this.instance = instance;
        this.wixCodeApi = wixCodeApi;
        this.appParams = appParams;
        this.experiments = experiments;
        this.httpClient = httpClient;
        withErrorHandler({ httpClient: httpClient, errorHandler: errorHandler });
        this.environment = getFlowApiEnvironment(wixCodeApi);
        var siteRevision = wixCodeApi.site.revision;
        var csrfToken = platformAPIs.getCsrfToken();
        this.httpAdapter = httpAdapterFactory(instance, siteRevision, csrfToken);
        var baseUrl = getServerBaseUrl({
            wixCodeApi: this.wixCodeApi,
            appParams: this.appParams,
        });
        this.bookingsCheckoutApiConfig = createConfig({
            experiments: experiments,
            getBaseUrl: function () { return baseUrl; },
            getAuthorization: function () {
                return getInstance({
                    wixCodeApi: _this.wixCodeApi,
                    appParams: _this.appParams,
                });
            },
            httpClient: httpClient,
        });
        this.availabilityCalendarServer = AvailabilityCalendar("".concat(baseUrl).concat(AVAILABILITY_SERVER_URL));
        this.catalogServer = ServicesCatalogServer("".concat(baseUrl).concat(CATALOG_SERVER_URL));
    }
    BookingsAPI.prototype.getTranslations = function (language) {
        return __awaiter(this, void 0, void 0, function () {
            var staticsBaseUrl;
            return __generator(this, function (_a) {
                staticsBaseUrl = this.appParams.baseUrls && this.appParams.baseUrls.platformAppsBaseUrl;
                return [2 /*return*/, this.httpAdapter.get("".concat(staticsBaseUrl, "assets/locale/messages_").concat(language, ".json"), null)];
            });
        });
    };
    BookingsAPI.prototype.getStaffList = function () {
        return __awaiter(this, void 0, void 0, function () {
            var staffMembersResponse;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.httpClient.request(queryStaffMembers({}))];
                    case 1:
                        staffMembersResponse = _a.sent();
                        return [2 /*return*/, {
                                resources: staffMembersResponse.data.staffMembers.map(mapStaffV1ToStaffDto),
                            }];
                }
            });
        });
    };
    BookingsAPI.prototype.notifyOwnerNonPremium = function (service) {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var serverBaseUrl, notificationService;
            return __generator(this, function (_b) {
                serverBaseUrl = getServerBaseUrl({
                    wixCodeApi: this.wixCodeApi,
                    appParams: this.appParams,
                });
                notificationService = NotificationsServer("".concat(serverBaseUrl).concat(NOTIFICATIONS_SERVER_URL)).NotificationsSettings();
                return [2 /*return*/, notificationService({
                        authorization: this.instance,
                    }).missedBooking({
                        serviceType: mapServiceTypeV1ToV2((_a = service === null || service === void 0 ? void 0 : service.info) === null || _a === void 0 ? void 0 : _a.type),
                    })];
            });
        });
    };
    BookingsAPI.prototype.getCatalogData = function () {
        var _a, _b, _c, _d, _e, _f;
        return __awaiter(this, void 0, void 0, function () {
            var _g, servicesResponse, businessInfoResponse;
            return __generator(this, function (_h) {
                switch (_h.label) {
                    case 0: return [4 /*yield*/, Promise.all([
                            this.httpClient
                                .request(queryServices({
                                query: {
                                    filter: {
                                        $and: [
                                            { $or: [{ hidden: false }, { hidden: { $exists: false } }] },
                                            { type: ServiceV2Type.CLASS },
                                        ],
                                    },
                                },
                            }))
                                .catch(function (e) {
                                console.error('Failed to fetch services', e);
                                return undefined;
                            }),
                            this.catalogServer
                                .Bulk()({
                                Authorization: this.instance,
                            })
                                .get({
                                requestBusiness: {
                                    suppressNotFoundError: false,
                                },
                            }),
                        ])];
                    case 1:
                        _g = _h.sent(), servicesResponse = _g[0], businessInfoResponse = _g[1];
                        return [2 /*return*/, {
                                services: (_c = (_b = (_a = servicesResponse === null || servicesResponse === void 0 ? void 0 : servicesResponse.data) === null || _a === void 0 ? void 0 : _a.services) === null || _b === void 0 ? void 0 : _b.map(mapServiceV2ToService)) !== null && _c !== void 0 ? _c : [],
                                businessInfo: mapResponseToBusinessInfo((_d = businessInfoResponse === null || businessInfoResponse === void 0 ? void 0 : businessInfoResponse.responseBusiness) !== null && _d !== void 0 ? _d : {}),
                                activeFeatures: (_f = (_e = businessInfoResponse === null || businessInfoResponse === void 0 ? void 0 : businessInfoResponse.responseBusiness) === null || _e === void 0 ? void 0 : _e.activeFeatures) !== null && _f !== void 0 ? _f : {},
                            }];
                }
            });
        });
    };
    BookingsAPI.prototype.getDefaultTimezone = function (catalogData) {
        var shouldUseBusinessTimezone = catalogData.businessInfo.timezoneProperties.defaultTimezone ===
            TimezoneType.BUSINESS;
        var businessTimezone = catalogData.businessInfo.timeZone;
        return shouldUseBusinessTimezone ? businessTimezone : getLocalTimezone();
    };
    BookingsAPI.prototype.getNextAvailableDate = function (catalogData, settings) {
        var _a, _b, _c, _d, _e, _f;
        return __awaiter(this, void 0, void 0, function () {
            var timezone, startOfDayAtSelectedTimezone, availabilityCalendarService, monthRange, from, to, slotAvailability, e_1;
            return __generator(this, function (_g) {
                switch (_g.label) {
                    case 0:
                        timezone = this.getDefaultTimezone(catalogData);
                        startOfDayAtSelectedTimezone = getStartOfDayInSelectedTimezone(timezone);
                        _g.label = 1;
                    case 1:
                        _g.trys.push([1, 3, , 4]);
                        availabilityCalendarService = this.availabilityCalendarServer.AvailabilityCalendar();
                        monthRange = 4;
                        from = startOfDayAtSelectedTimezone;
                        to = getEndOfMonthShiftedRfcString(startOfDayAtSelectedTimezone, monthRange);
                        return [4 /*yield*/, availabilityCalendarService({
                                Authorization: this.instance,
                            }).queryAvailability(this.createSlotAvailabilityRequest(catalogData, settings, from, to, true))];
                    case 2:
                        slotAvailability = _g.sent();
                        return [2 /*return*/, ((_c = (_b = (_a = slotAvailability === null || slotAvailability === void 0 ? void 0 : slotAvailability.availabilityEntries) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.slot) === null || _c === void 0 ? void 0 : _c.startDate)
                                ? getShiftedRfcStringDateStart((_f = (_e = (_d = slotAvailability === null || slotAvailability === void 0 ? void 0 : slotAvailability.availabilityEntries) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.slot) === null || _f === void 0 ? void 0 : _f.startDate)
                                : startOfDayAtSelectedTimezone];
                    case 3:
                        e_1 = _g.sent();
                        console.log('failed to fetch first available slot', e_1);
                        return [2 /*return*/, startOfDayAtSelectedTimezone];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    BookingsAPI.prototype.getAvailabilitySlots = function (catalogData, rfcDateTime, settings) {
        return __awaiter(this, void 0, void 0, function () {
            var availabilityCalendarService, availability, e_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        availabilityCalendarService = this.availabilityCalendarServer.AvailabilityCalendar();
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, availabilityCalendarService({
                                Authorization: this.instance,
                            }).queryAvailability(this.createSlotAvailabilityRequest(catalogData, settings, rfcDateTime, rfcDateTime.replace('00:00:00', '23:59:59')))];
                    case 2:
                        availability = _a.sent();
                        return [2 /*return*/, availability.availabilityEntries];
                    case 3:
                        e_2 = _a.sent();
                        console.log('failed to fetch availability', e_2);
                        return [2 /*return*/, []];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    BookingsAPI.prototype.getBookings = function () {
        return __awaiter(this, void 0, void 0, function () {
            var currentUser;
            return __generator(this, function (_a) {
                try {
                    currentUser = this.wixCodeApi.user.currentUser;
                    if (currentUser.loggedIn &&
                        !this.environment.isEditorMode &&
                        !this.environment.isPreviewMode) {
                        return [2 /*return*/, queryBookings({
                                startDate: getTodayStartOfDayUTC(),
                                status: BookingStatus.CONFIRMED,
                                serviceType: ServiceType.GROUP,
                            }, this.bookingsCheckoutApiConfig)];
                    }
                    else {
                        return [2 /*return*/, []];
                    }
                }
                catch (e) {
                    console.log('failed to fetch members bookings', e);
                    return [2 /*return*/, []];
                }
                return [2 /*return*/];
            });
        });
    };
    BookingsAPI.prototype.createSlotAvailabilityRequest = function (catalogData, settings, fromRfcString, toRfcString, nextAvailable) {
        if (nextAvailable === void 0) { nextAvailable = false; }
        var selectedServices = settings[DailyTimetableSettingsKeys.SELECTED_SERVICES];
        var filteredServices = selectedServices
            ? catalogData.services.filter(function (service) { return selectedServices[service.id]; })
            : catalogData.services;
        var serviceIds = filteredServices.map(function (service) { return service.id; });
        return {
            timezone: this.getDefaultTimezone(catalogData),
            query: __assign({ filter: __assign({ serviceId: serviceIds, startDate: fromRfcString, endDate: toRfcString }, (nextAvailable
                    ? {
                        openSpots: { $gte: '1' },
                        'bookingPolicyViolations.tooLateToBook': false,
                    }
                    : {})) }, (nextAvailable
                ? {
                    cursorPaging: { limit: 1 },
                }
                : {})),
        };
    };
    return BookingsAPI;
}());
export { BookingsAPI };
var mapServiceTypeV1ToV2 = function (serviceType) {
    var _a;
    var serviceTypesMap = (_a = {},
        _a[ServiceType.INDIVIDUAL] = ServiceV2Type.APPOINTMENT,
        _a[ServiceType.GROUP] = ServiceV2Type.CLASS,
        _a[ServiceType.COURSE] = ServiceV2Type.COURSE,
        _a);
    return serviceTypesMap[serviceType] || ServiceV2Type.UNKNOWN_SERVICE_TYPE;
};
//# sourceMappingURL=bookingsAPI.js.map